import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ClickHandlerService } from '@services/ui/click-handler.service';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { SharedModule } from '@shared/modules/shared.module';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-item-menu',
	imports: [SharedModule, AvatarComponent, TooltipComponent],
	templateUrl: './item-menu.component.html',
	styleUrl: './item-menu.component.scss',
})
export class ItemMenuComponent implements OnInit, OnDestroy {
	@Input() item!: any;
	@Input() index = 0;
	@Input() root = false;
	@Input() isSidebarOpen = true;
	@Output() itemClick = new EventEmitter<any>();
	@Output() submenuExpanded = new EventEmitter<ElementRef>();
	@Input() forceExpanded = false;
	@Input() isActive = false;
	@ViewChild('submenuTemplate') submenuTemplate!: any;

	expanded = false;
	private overlayRef: OverlayRef | null = null;
	private clickSubscription = new Subscription();

	constructor(
		private overlay: Overlay,
		private elRef: ElementRef,
		private viewContainerRef: ViewContainerRef,
		private clickHandlerService: ClickHandlerService,
		private sanitizer: DomSanitizer,
	) {}

	toggleSubmenu(event?: Event) {
		if (this.item.items && this.item.items.length > 0) {
			this.expanded = !this.expanded;
			// Stop event propagation to prevent handleClick execution
			if (event) {
				event.stopPropagation();
			}
		}
	}

	ngOnInit(): void {
		this.clickSubscription = this.clickHandlerService.click$.subscribe((event: Event) => {
			if (!this.elRef.nativeElement.contains(event.target)) {
				this.expanded = false;
				this.closeOverlay();
			}
		});
	}

	ngOnDestroy(): void {
		this.clickSubscription.unsubscribe();
		this.closeOverlay();
	}

	handleClick(event?: Event): void {
		if (this.item.action) {
			// If there's a custom action, prevent navigation and execute the action
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			this.item.action();
			this.itemClick.emit(this.item);
			return;
		} else if (this.item.items && this.item.items.length > 0) {
			// Prevent navigation if the item has submenus
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			// Profile always uses floating submenu, regardless of sidebar state
			if (this.item.id === 'profile') {
				this.toggleOverlayMenu();
			} else if (this.isSidebarOpen) {
				const wasExpanded = this.expanded;
				this.expanded = !this.expanded;
				// Emit event when submenu is expanded (not collapsed)
				if (!wasExpanded && this.expanded) {
					setTimeout(() => {
						this.submenuExpanded.emit(this.elRef);
					}, 0);
				}
			} else {
				this.toggleOverlayMenu();
			}
		} else {
			this.itemClick.emit(this.item);
		}
	}

	toggleOverlayMenu(): void {
		if (this.overlayRef) {
			this.closeOverlay();
			return;
		}

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(this.elRef.nativeElement)
			.withPositions([
				{
					originX: 'start',
					originY: 'bottom',
					overlayX: 'start',
					overlayY: 'top',
					offsetX: 12, // Align with icon (12px padding)
					offsetY: 8, // Space below the item
				},
			]);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.close(),
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
		});

		const portal = new TemplatePortal(this.submenuTemplate, this.viewContainerRef);
		this.overlayRef.attach(portal);
		this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());
	}

	closeOverlay(): void {
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}

	getSanitizedSvg(svgContent: string): SafeHtml {
		return this.sanitizer.bypassSecurityTrustHtml(svgContent);
	}
}
